import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { registerUser, isUsernameValid, isUsernameAvailable, setUsernameForUser } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import styles from './RegisterPage.module.css';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';

function RegisterPage() {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [username, setUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<'idle'|'checking'|'valid'|'taken'|'invalid'>('idle');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [privacyConsent, setPrivacyConsent] = useState(false); // KVKK onayı için state
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!displayName || !email || !password || !passwordConfirm) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }

    if (password !== passwordConfirm) {
      setError('Şifreler eşleşmiyor.');
      return;
    }

    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }

    // KVKK onayı kontrolü
    if (!privacyConsent) {
      setError('Devam etmek için kişisel verilerinizin işlenmesine onay vermeniz gerekmektedir.');
      return;
    }

    // Username validation
    if (!username) {
      setError('Kullanıcı adı gerekli.');
      return;
    }
    if (!isUsernameValid(username)) {
      setError('Kullanıcı adı geçersiz format. (3-24 karakter, sadece küçük harf ve rakam)');
      return;
    }
    if (usernameStatus === 'taken' || usernameStatus === 'invalid' || usernameStatus === 'checking') {
      setError('Kullanıcı adı uygun değil.');
      return;
    }

    try {
      setLoading(true);
      const user = await registerUser(email, password, displayName);
      // Set username after registration
      try { await setUsernameForUser(user.uid, username); } catch {}
      navigate(`/profile/${username}`);
    } catch (err: any) {
      setError(err.message || 'Kayıt olurken bir hata oluştu.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = () => {
    navigate('/');
  };

  const handleGoogleError = (error: Error) => {
    setError(error.message || 'Google ile kayıt olurken bir hata oluştu.');
  };

  // Auto-generate suggestions based on display name
  useEffect(()=>{
    let active = true;
    const run = async () => {
      const base = displayName.trim().toLowerCase()
        .replace(/ğ/g,'g').replace(/ü/g,'u').replace(/ş/g,'s').replace(/ı/g,'i').replace(/ö/g,'o').replace(/ç/g,'c')
        .replace(/[^a-z0-9 ]/g,'')
        .replace(/\s+/g,' ');
      if (!base) { if(active) setSuggestions([]); return; }
      const parts = base.split(' ');
      const core = (parts[0] || '').slice(0,12);
      const alt = parts.length>1 ? (core + (parts[1][0]||'')).slice(0,14) : core;
      const rand = Math.floor(Math.random()*900 + 100); // 3-digit
      let proposalSet = Array.from(new Set([
        core,
        alt,
        core+rand,
        alt+rand,
        (core+Date.now().toString().slice(-2)),
        core+Math.floor(rand/3)
      ])).filter(s=>isUsernameValid(s)).slice(0,8);
      // Check availability in parallel
      const results = await Promise.all(proposalSet.map(p=>isUsernameAvailable(p)));
  proposalSet = proposalSet.filter((_,i)=>results[i]);
      // If empty, brute force add random suffixes until we get some available (max 5 attempts)
      let attempts = 0;
      while (proposalSet.length === 0 && attempts < 5) {
        attempts++;
        const candidate = (core || 'user') + Math.floor(Math.random()*1000).toString().padStart(2,'0');
        if (isUsernameValid(candidate) && await isUsernameAvailable(candidate)) {
          proposalSet.push(candidate);
        }
      }
      if (!active) return;
      const finalList = proposalSet.slice(0,5);
      setSuggestions(finalList);
      if (!username && finalList.length>0) setUsername(finalList[0]);
  };
  const timeout = setTimeout(run, 600); // debounce after user stops typing
  return ()=>{ active=false; clearTimeout(timeout); };
  }, [displayName]);

  // Username availability debounce
  useEffect(()=>{
    if (!username) { setUsernameStatus('idle'); return; }
    if (!isUsernameValid(username)) { setUsernameStatus('invalid'); return; }
    let active = true;
    setUsernameStatus('checking');
    const t = setTimeout(async ()=>{
      try {
        const available = await isUsernameAvailable(username);
        if (!active) return;
        setUsernameStatus(available ? 'valid' : 'taken');
      } catch {
        if (!active) return;
        setUsernameStatus('taken');
      }
    }, 500);
    return ()=>{ active=false; clearTimeout(t); };
  }, [username]);

  return (
    <div className={styles.registerPage}>
      <div className={styles.registerShell}>
        <div className={styles.headArea}>
          <img src="/logo.png" alt="vagonla" className={styles.brandLogo} />
          <h1 className={styles.pageTitle}>Hesabını Oluştur</h1>
          <p className={styles.pageSubtitle}>Dakikalar içinde katıl, hatları ve durakları favorilerine ekle.</p>
        </div>
        <div className={styles.formCardGlass}>
          {error && <div className={styles.errorBox}>{error}</div>}
          <form onSubmit={handleSubmit} className={styles.formLayout}>
            <div className={styles.fieldGroup}>
              <label htmlFor="displayName" className={styles.fieldLabel}>Ad Soyad</label>
              <input
                type="text"
                id="displayName"
                className={styles.pillInput}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={loading}
                placeholder="Adınız ve soyadınız"
                required
              />
            </div>
            <div className={styles.fieldGroup}>
              <label htmlFor="email" className={styles.fieldLabel}>E‑posta</label>
              <input
                type="email"
                id="email"
                className={styles.pillInput}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                placeholder="ornek@eposta.com"
                required
              />
            </div>
            <div className={styles.fieldGroup}>
              <label htmlFor="username" className={styles.fieldLabel}>Kullanıcı Adı</label>
              <input
                type="text"
                id="username"
                className={styles.pillInput}
                value={username}
                onChange={e=>setUsername(e.target.value.toLowerCase())}
                disabled={loading}
                placeholder="ör: mehmet123"
                minLength={3}
                maxLength={24}
                required
              />
              {usernameStatus !== 'idle' && (
                <div className={
                  usernameStatus === 'valid' ? styles.userOk :
                  usernameStatus === 'taken' ? styles.userErr :
                  usernameStatus === 'invalid' ? styles.userErr : styles.userInfo
                }>
                  {usernameStatus === 'checking' && 'Kontrol ediliyor...'}
                  {usernameStatus === 'valid' && 'Uygun'}
                  {usernameStatus === 'taken' && 'Alınmış'}
                  {usernameStatus === 'invalid' && 'Geçersiz'}
                </div>
              )}
              {suggestions.length>0 && (
                <div className={styles.suggestionRow}>
                  {suggestions.map(s => (
                    <button type="button" key={s} disabled={loading} className={styles.suggestionChip} onClick={()=>setUsername(s)}>{s}</button>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.dualRow}>
              <div className={styles.fieldGroup}>
                <label htmlFor="password" className={styles.fieldLabel}>Şifre</label>
                <input
                  type="password"
                  id="password"
                  className={styles.pillInput}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  placeholder="En az 6 karakter"
                  required
                  minLength={6}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label htmlFor="passwordConfirm" className={styles.fieldLabel}>Tekrar</label>
                <input
                  type="password"
                  id="passwordConfirm"
                  className={styles.pillInput}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  disabled={loading}
                  placeholder="Şifreyi tekrar"
                  required
                  minLength={6}
                />
              </div>
            </div>
            <div className={styles.consentRow}>
              <label className={styles.consentPill}>
                <input
                  type="checkbox"
                  checked={privacyConsent}
                  onChange={(e) => setPrivacyConsent(e.target.checked)}
                  disabled={loading}
                  required
                />
                <span className={styles.checkIcon} />
                <span className={styles.consentText}>KVKK <a href="https://vagonla.com/kvkk" target="_blank" rel="noopener noreferrer">aydınlatma metnini</a> okudum ve onaylıyorum.</span>
              </label>
            </div>
            <button type="submit" className={styles.primaryAction} disabled={loading}>
              {loading ? 'Kaydediliyor…' : 'Kayıt Ol'}
            </button>
          </form>
          <div className={styles.inlineDivider}><span>veya</span></div>
          <GoogleSignInButton
            text="Google ile Devam Et"
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
          />
          <div className={styles.altAction}>Zaten hesabın var mı? <Link to="/login">Giriş yap</Link></div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
